const DB_NAME = 'PlantInventoryLocal';
const DB_VERSION = 1;
const PLANTS_STORE = 'plants';
const PHOTOS_STORE = 'photos';
const MAX_PLANTS = 100;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(PLANTS_STORE)) {
        db.createObjectStore(PLANTS_STORE, { keyPath: 'plant_id' });
      }
      if (!db.objectStoreNames.contains(PHOTOS_STORE)) {
        db.createObjectStore(PHOTOS_STORE, { keyPath: 'photo_id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => { dbPromise = null; reject(request.error); };
  });
  return dbPromise;
}

// ---------- Plants ----------

export async function getAllPlants() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PLANTS_STORE, 'readonly');
    const store = t.objectStore(PLANTS_STORE);
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getPlant(plantId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PLANTS_STORE, 'readonly');
    const store = t.objectStore(PLANTS_STORE);
    const req = store.get(plantId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function addPlant(plantData) {
  const count = await getPlantCount();
  if (count >= MAX_PLANTS) {
    throw new Error(`You've reached the limit of ${MAX_PLANTS} plants in local mode. Export your collection as CSV and delete some plants to make room, or sign in with Google for unlimited storage.`);
  }
  const id = plantData.plant_id || `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const record = { ...plantData, plant_id: id, created_at: new Date().toISOString() };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PLANTS_STORE, 'readwrite');
    const store = t.objectStore(PLANTS_STORE);
    const req = store.put(record);
    req.onsuccess = () => resolve(record);
    req.onerror = () => reject(req.error);
  });
}

export async function updatePlant(plantId, plantData) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PLANTS_STORE, 'readwrite');
    const store = t.objectStore(PLANTS_STORE);
    const getReq = store.get(plantId);
    getReq.onerror = () => reject(getReq.error);
    getReq.onsuccess = () => {
      const existing = getReq.result;
      if (!existing) { reject(new Error('Plant not found')); return; }
      const updated = { ...existing, ...plantData, plant_id: plantId, updated_at: new Date().toISOString() };
      const putReq = store.put(updated);
      putReq.onsuccess = () => resolve(updated);
      putReq.onerror = () => reject(putReq.error);
    };
  });
}

export async function deletePlant(plantId) {
  try { await deleteAllPhotos(plantId); } catch (err) {
    // No photos to delete is a normal case — silently continue
    console.debug('deleteAllPhotos during plant delete (non-fatal):', err?.message);
  }
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PLANTS_STORE, 'readwrite');
    const store = t.objectStore(PLANTS_STORE);
    const req = store.delete(plantId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Merge two local-mode plants: absorb `sourcePlantId` into
 * `targetPlantId`. Applies the client-supplied winning fields, and
 * either (a) if `selectedPhotos` is provided, sets the target's
 * photos to exactly that selection in the given order (cover =
 * first), or (b) falls back to legacy behavior — appends source's
 * photos onto the target up to MAX_PHOTOS_PER_PLANT. Then deletes
 * the source plant. Returns `{ dropped_photo_count }` for UI
 * feedback so we mirror the backend endpoint's shape.
 *
 * `selectedPhotos` shape: array of `{ plantId, index, image_base64 }`
 * in cover order. Photos from either plant that aren't in the
 * selection are dropped (source's are gone with the plant delete;
 * target's unselected ones get physically removed to keep IndexedDB
 * consistent with the new photo_order).
 */
export async function mergePlants(targetPlantId, sourcePlantId, { common_name, scientific_name, care_details, selectedPhotos } = {}) {
  if (targetPlantId === sourcePlantId) {
    throw new Error('Cannot merge a plant with itself.');
  }
  // Update the target's care fields first so the UI reflects the wins
  // as soon as the source is gone. Care fields are MERGED not replaced
  // so any care_detail the diff UI doesn't expose (dormancy_months,
  // dormancy_checked, bloom_checked, …) survives the merge.
  const patch = {};
  if (common_name !== undefined) patch.common_name = common_name;
  if (scientific_name !== undefined) patch.scientific_name = scientific_name;
  if (care_details !== undefined) {
    const target = await getPlant(targetPlantId);
    const existingCare = (target && target.care_details) || {};
    patch.care_details = { ...existingCare, ...care_details };
  }
  patch.incomplete = false;
  await updatePlant(targetPlantId, patch);

  let dropped = 0;

  if (Array.isArray(selectedPhotos)) {
    // Explicit picker mode. Rebuild the target's photo list from the
    // user's selection (up to the 5-cap).
    const targetPhotos = await getAllPhotos(targetPlantId);
    const targetByBase64 = new Map(targetPhotos.map(p => [p.image_base64, p]));

    // 1) Remove any target photos the user unchecked (they're not in
    //    the selection). We do this by deleting all target photos
    //    then re-adding the selected ones in order — simplest way to
    //    guarantee the resulting order matches the picker choices.
    const capped = selectedPhotos.slice(0, MAX_PHOTOS_PER_PLANT);
    if (capped.length < selectedPhotos.length) dropped += (selectedPhotos.length - capped.length);

    // Wipe the target's existing photos so we can re-insert in the
    // user-chosen order. The `savePhoto` upserts the primary photo at
    // the legacy key (plantId); `addPhotoToPlant` writes index N.
    await deleteAllPhotos(targetPlantId);

    // Also collect the source's picked photos so they get moved onto
    // the target BEFORE we delete the source plant (which nukes its
    // remaining photos in IndexedDB).
    for (let i = 0; i < capped.length; i++) {
      const p = capped[i];
      const b64 = p.image_base64;
      if (!b64) continue;
      if (i === 0) {
        // Primary/cover slot — use savePhoto for the legacy index=0 key
        await savePhoto(targetPlantId, b64);
      } else {
        try { await addPhotoToPlant(targetPlantId, b64); }
        catch (err) { console.warn('mergePlants: attach picked photo failed:', err?.message || err); dropped += 1; }
      }
    }
    // Keep the plant's has_photo flag in sync.
    await updatePlant(targetPlantId, { has_photo: capped.length > 0 });
  } else {
    // Legacy behavior — dedupe source onto target up to 5.
    const targetPhotos = await getAllPhotos(targetPlantId);
    const sourcePhotos = await getAllPhotos(sourcePlantId);
    const existingBase64 = new Set(targetPhotos.map(p => p.image_base64));
    let attached = targetPhotos.length;
    for (const p of sourcePhotos) {
      if (existingBase64.has(p.image_base64)) { continue; }
      if (attached >= MAX_PHOTOS_PER_PLANT) { dropped += 1; continue; }
      try {
        await addPhotoToPlant(targetPlantId, p.image_base64);
        attached += 1;
      } catch (err) {
        console.warn('mergePlants: failed to attach source photo:', err?.message || err);
        dropped += 1;
      }
    }
  }

  // Wipe the source (this also deletes its photos in IndexedDB).
  await deletePlant(sourcePlantId);
  return { dropped_photo_count: dropped };
}

export async function getPlantCount() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PLANTS_STORE, 'readonly');
    const store = t.objectStore(PLANTS_STORE);
    const req = store.count();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------- Photos ----------

export async function savePhoto(plantId, base64Data) {
  // Generate thumbnail BEFORE opening the transaction to avoid async gap
  let thumbnailData;
  try {
    thumbnailData = await generateThumbnail(base64Data, 200);
  } catch (_) {
    thumbnailData = base64Data;
  }
  const record = { photo_id: plantId, image_base64: base64Data, thumbnail_base64: thumbnailData };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = t.objectStore(PHOTOS_STORE);
    const req = store.put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getPhoto(plantId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readonly');
    const store = t.objectStore(PHOTOS_STORE);
    const req = store.get(plantId);
    req.onsuccess = () => resolve(req.result ? req.result.image_base64 : null);
    req.onerror = () => reject(req.error);
  });
}

export async function getPhotoThumbnail(plantId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readonly');
    const store = t.objectStore(PHOTOS_STORE);
    const req = store.get(plantId);
    req.onsuccess = () => {
      const rec = req.result;
      resolve(rec ? (rec.thumbnail_base64 || rec.image_base64) : null);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function deletePhoto(plantId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = t.objectStore(PHOTOS_STORE);
    const req = store.delete(plantId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

// ---------- Care Log (local) ----------
// Mirrors the Drive-mode `POST/DELETE /api/plants/{id}/care-log` endpoints.
// Log is capped at 100 entries per plant so IndexedDB storage stays bounded.

export async function addLocalCareLogEntry(plantId, action, note = null) {
  const plant = await getPlant(plantId);
  if (!plant) throw new Error('Plant not found');
  const entry = {
    entry_id: (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    action,
    at: new Date().toISOString(),
    note,
  };
  const log = Array.isArray(plant.care_log) ? [...plant.care_log, entry] : [entry];
  const trimmed = log.length > 100 ? log.slice(-100) : log;
  await updatePlant(plantId, { ...plant, care_log: trimmed, updated_at: new Date().toISOString() });
  return trimmed;
}

export async function deleteLocalCareLogEntry(plantId, entryId) {
  const plant = await getPlant(plantId);
  if (!plant) throw new Error('Plant not found');
  const log = (plant.care_log || []).filter((e) => e.entry_id !== entryId);
  await updatePlant(plantId, { ...plant, care_log: log, updated_at: new Date().toISOString() });
  // Best-effort cleanup of the attached IndexedDB photo, if any. Silent on
  // failure — an orphan photo record is preferable to a broken UI.
  try {
    const photoKey = `${plantId}_care_${entryId}`;
    await deletePhoto(photoKey);
  } catch (_e) {
    // ignore — deletePhoto returns cleanly for missing keys anyway
  }
  return log;
}

// Care log photos are stored in the same IndexedDB `photos` store as plant
// photos, but keyed as `${plantId}_care_${entryId}` so they never collide
// with cover-photo slots (which use `plantId`, `plantId_1`, etc.). Reuses
// the existing savePhoto/getPhoto primitives.
export async function attachLocalCareLogPhoto(plantId, entryId, base64Data) {
  const plant = await getPlant(plantId);
  if (!plant) throw new Error('Plant not found');
  const log = Array.isArray(plant.care_log) ? [...plant.care_log] : [];
  const idx = log.findIndex((e) => e.entry_id === entryId);
  if (idx < 0) throw new Error('Care log entry not found');
  const photoKey = `${plantId}_care_${entryId}`;
  await savePhoto(photoKey, base64Data);
  // Mark the entry as having a photo. We store the local key under
  // `photo_drive_id` so the same field name works across Drive and Local
  // modes — the client just checks for presence to decide whether to
  // render a thumb, and the fetch path differs by mode.
  log[idx] = { ...log[idx], photo_drive_id: photoKey };
  await updatePlant(plantId, { ...plant, care_log: log, updated_at: new Date().toISOString() });
  return log;
}

// Edit an existing care log entry's note (only). Notes are optional
// free-text ≤140 chars; empty string is normalized to null.
export async function editLocalCareLogEntryNote(plantId, entryId, note) {
  const plant = await getPlant(plantId);
  if (!plant) throw new Error('Plant not found');
  const log = Array.isArray(plant.care_log) ? [...plant.care_log] : [];
  const idx = log.findIndex((e) => e.entry_id === entryId);
  if (idx < 0) throw new Error('Care log entry not found');
  const trimmed = typeof note === 'string' ? note.trim().slice(0, 140) : null;
  log[idx] = { ...log[idx], note: trimmed || null };
  await updatePlant(plantId, { ...plant, care_log: log, updated_at: new Date().toISOString() });
  return log;
}

// Copy a care log entry's attached photo INTO the plant's cover carousel
// at index 0. Original care log entry keeps its photo — the two references
// are independent (deleting the entry later must NOT delete the cover).
// Reuses the same key convention as `addPhotoToPlant`: cover=plantId,
// second=plantId_1, etc. Fails if the cover carousel is at MAX_PHOTOS.
export async function promoteLocalCareLogPhotoToCover(plantId, entryId) {
  const plant = await getPlant(plantId);
  if (!plant) throw new Error('Plant not found');
  const log = Array.isArray(plant.care_log) ? [...plant.care_log] : [];
  const entry = log.find((e) => e.entry_id === entryId);
  if (!entry) throw new Error('Care log entry not found');
  const sourceKey = entry.photo_drive_id;
  if (!sourceKey) throw new Error('This entry has no photo to promote');
  const sourceBase64 = await getPhoto(sourceKey);
  if (!sourceBase64) throw new Error('Photo not found in local storage');
  // Reuse the existing addPhotoToPlant primitive — it enforces the cap and
  // updates has_photo. Since our caller wants the promoted photo as the
  // COVER (index 0), we then swap it into slot 0 via setPhotoAsCover.
  const newIndex = await addPhotoToPlant(plantId, sourceBase64);
  if (newIndex !== 0) {
    await setPhotoAsCover(plantId, newIndex);
  }
  // has_photo may already be true; ensure it is set.
  await updatePlant(plantId, { has_photo: true });
  return true;
}

// ---------- CSV Export (local) ----------

function capitalizeTemp(s) {
  if (!s) return s;
  return s.replace(/°f\b/gi, '°F').replace(/°c\b/gi, '°C');
}

export async function exportLocalCSV() {
  const plants = await getAllPlants();
  if (plants.length === 0) throw new Error('No plants to export');
  const headers = ['Common Name', 'Scientific Name', 'Family', 'Type', 'Watering', 'Light', 'Humidity', 'Temperature', 'Toxicity', 'Growth Rate', 'Dormancy', 'Photo', 'Status'];
  const rows = plants.map(p => [
    p.common_name || '',
    p.scientific_name || '',
    p.care_details?.family || '',
    p.care_details?.plant_type || '',
    p.care_details?.watering || '',
    p.care_details?.light || '',
    p.care_details?.humidity || '',
    capitalizeTemp(p.care_details?.temperature || ''),
    p.care_details?.toxicity || '',
    p.care_details?.growth_rate || '',
    p.care_details?.dormancy_notes || '',
    p.has_photo ? '(stored on device)' : '',
    p.incomplete ? 'Incomplete' : 'Complete'
  ]);

  const escape = (v) => {
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `plants_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ---------- Helpers ----------

function generateThumbnail(base64Data, maxSize) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.7).split(',')[1]);
    };
    img.onerror = reject;
    img.src = `data:image/jpeg;base64,${base64Data}`;
  });
}

export { MAX_PLANTS };

// ---------- Multi-Photo Support ----------

const MAX_PHOTOS_PER_PLANT = 5;

/**
 * Get all photos for a plant. Returns array of { index, image_base64, thumbnail_base64 }.
 * Backward compat: old photos keyed by plantId become index 0.
 */
export async function getAllPhotos(plantId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readonly');
    const store = t.objectStore(PHOTOS_STORE);
    const results = [];
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        const key = cursor.value.photo_id;
        // Match: exact plantId (legacy index 0) or plantId_N pattern
        if (key === plantId) {
          results.push({ index: 0, ...cursor.value });
        } else if (key.startsWith(plantId + '_')) {
          const idx = parseInt(key.split('_').pop(), 10);
          if (!isNaN(idx)) results.push({ index: idx, ...cursor.value });
        }
        cursor.continue();
      } else {
        results.sort((a, b) => a.index - b.index);
        resolve(results);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Add an additional photo to an existing plant.
 * Returns the new photo index.
 */
export async function addPhotoToPlant(plantId, base64Data) {
  const existing = await getAllPhotos(plantId);
  if (existing.length >= MAX_PHOTOS_PER_PLANT) {
    throw new Error(`Maximum ${MAX_PHOTOS_PER_PLANT} photos per plant`);
  }
  // Compute next index
  const nextIndex = existing.length === 0 ? 0 : Math.max(...existing.map(p => p.index)) + 1;
  const photoKey = nextIndex === 0 ? plantId : `${plantId}_${nextIndex}`;

  let thumbnailData;
  try {
    thumbnailData = await generateThumbnail(base64Data, 200);
  } catch (_) {
    thumbnailData = base64Data;
  }
  const record = { photo_id: photoKey, image_base64: base64Data, thumbnail_base64: thumbnailData };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = t.objectStore(PHOTOS_STORE);
    const req = store.put(record);
    req.onsuccess = () => resolve(nextIndex);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Delete a specific photo by index from a plant.
 */
export async function deletePhotoByIndex(plantId, photoIndex) {
  const photoKey = photoIndex === 0 ? plantId : `${plantId}_${photoIndex}`;
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = t.objectStore(PHOTOS_STORE);
    const req = store.delete(photoKey);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  // If we just deleted the cover record (key === plantId) and there
  // are still photos left, promote the lowest-index remaining record
  // into the cover slot. Otherwise the plant CARD in the library grid
  // falls back to the leaf placeholder even though the modal still
  // shows the remaining photos.
  if (photoIndex === 0) {
    const remaining = await getAllPhotos(plantId);
    if (remaining.length > 0) {
      // getAllPhotos returns results already sorted by index. After
      // deleting the cover, the lowest remaining record now lives
      // under key `${plantId}_${idx}` — move its payload to `plantId`.
      const promote = remaining[0];
      const oldKey = `${plantId}_${promote.index}`;
      await new Promise((resolve, reject) => {
        const t = db.transaction(PHOTOS_STORE, 'readwrite');
        const store = t.objectStore(PHOTOS_STORE);
        store.put({
          photo_id: plantId,
          image_base64: promote.image_base64,
          thumbnail_base64: promote.thumbnail_base64,
        });
        store.delete(oldKey);
        t.oncomplete = () => resolve();
        t.onerror = () => reject(t.error);
      });
    }
  }
}

/**
 * Replace a specific photo by index with new image data.
 */
export async function replacePhotoByIndex(plantId, photoIndex, base64Data) {
  const photoKey = photoIndex === 0 ? plantId : `${plantId}_${photoIndex}`;
  let thumbnailData;
  try {
    thumbnailData = await generateThumbnail(base64Data, 200);
  } catch (_) {
    thumbnailData = base64Data;
  }
  const record = { photo_id: photoKey, image_base64: base64Data, thumbnail_base64: thumbnailData };
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = t.objectStore(PHOTOS_STORE);
    const req = store.put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Swap a non-cover photo (at photoIndex > 0) into the cover slot (index 0).
 * The record at `${plantId}_${photoIndex}` moves to key `plantId` (index 0),
 * and the existing cover moves to `${plantId}_${photoIndex}`.
 */
export async function setPhotoAsCover(plantId, photoIndex) {
  if (photoIndex === 0) return; // already the cover, no-op
  const coverKey = plantId;
  const otherKey = `${plantId}_${photoIndex}`;
  const db = await openDB();

  const [coverRec, otherRec] = await Promise.all([
    new Promise((res, rej) => {
      const t = db.transaction(PHOTOS_STORE, 'readonly');
      const req = t.objectStore(PHOTOS_STORE).get(coverKey);
      req.onsuccess = () => res(req.result || null);
      req.onerror = () => rej(req.error);
    }),
    new Promise((res, rej) => {
      const t = db.transaction(PHOTOS_STORE, 'readonly');
      const req = t.objectStore(PHOTOS_STORE).get(otherKey);
      req.onsuccess = () => res(req.result || null);
      req.onerror = () => rej(req.error);
    }),
  ]);

  if (!otherRec) return; // nothing to swap in

  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = t.objectStore(PHOTOS_STORE);
    // Move the chosen photo into the cover slot
    store.put({ ...otherRec, photo_id: coverKey });
    // Move old cover into the vacated slot, or delete the vacated key if
    // there was no prior cover record.
    if (coverRec) {
      store.put({ ...coverRec, photo_id: otherKey });
    } else {
      store.delete(otherKey);
    }
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/**
 * Reorder a plant's photos. `orderedIndexes` is the list of CURRENT photo
 * indexes in their new order (index 0 of the result becomes the cover).
 */
export async function reorderPhotos(plantId, orderedIndexes) {
  const photos = await getAllPhotos(plantId);
  const byIndex = new Map(photos.map((p) => [p.index, p]));
  const newRecords = orderedIndexes
    .map((oldIdx, newIdx) => {
      const rec = byIndex.get(oldIdx);
      if (!rec) return null;
      return {
        photo_id: newIdx === 0 ? plantId : `${plantId}_${newIdx}`,
        image_base64: rec.image_base64,
        thumbnail_base64: rec.thumbnail_base64,
      };
    })
    .filter(Boolean);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = t.objectStore(PHOTOS_STORE);
    for (const p of photos) store.delete(p.index === 0 ? plantId : `${plantId}_${p.index}`);
    for (const rec of newRecords) store.put(rec);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/**
 * Delete ALL photos for a plant (used when deleting the plant).
 */
export async function deleteAllPhotos(plantId) {
  const photos = await getAllPhotos(plantId);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PHOTOS_STORE, 'readwrite');
    const store = t.objectStore(PHOTOS_STORE);
    for (const photo of photos) {
      const key = photo.index === 0 ? plantId : `${plantId}_${photo.index}`;
      store.delete(key);
    }
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/**
 * Archive a plant with a reason.
 */
export async function archivePlant(plantId, reason) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PLANTS_STORE, 'readwrite');
    const store = t.objectStore(PLANTS_STORE);
    const req = store.get(plantId);
    req.onsuccess = () => {
      const plant = req.result;
      if (!plant) { resolve(); return; }
      plant.archived = true;
      plant.archive_reason = reason;
      plant.archived_at = new Date().toISOString();
      plant.updated_at = new Date().toISOString();
      store.put(plant);
    };
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

/**
 * Restore an archived plant.
 */
export async function restorePlant(plantId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(PLANTS_STORE, 'readwrite');
    const store = t.objectStore(PLANTS_STORE);
    const req = store.get(plantId);
    req.onsuccess = () => {
      const plant = req.result;
      if (!plant) { resolve(); return; }
      delete plant.archived;
      delete plant.archive_reason;
      delete plant.archived_at;
      plant.updated_at = new Date().toISOString();
      store.put(plant);
    };
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}


// ---------- Migration ----------

export async function clearAllData() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction([PLANTS_STORE, PHOTOS_STORE], 'readwrite');
    t.objectStore(PLANTS_STORE).clear();
    t.objectStore(PHOTOS_STORE).clear();
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}
